import Alert, { AlertVariant } from '@ds/components/feedback/Alert';
import Flex from '@ds/components/layout/Flex';
import { useAppMessages } from '@effects/useAppMessages';
import { IAppMessageCriteria } from '@typings';
import { resolveMessageText } from '@utils/app-message-matcher';
import { MessageType } from '@utils/enums';
import React from 'react';

import styles from './AppMessages.module.css';

interface Props {
    criteria: IAppMessageCriteria;
}

const LEVEL_TO_ALERT_VARIANT: Record<MessageType, AlertVariant> = {
    [MessageType.Info]: 'info',
    [MessageType.Warning]: 'warning',
    [MessageType.Error]: 'danger',
    [MessageType.Success]: 'success',
};

export const toAlertVariant = (level: MessageType): AlertVariant => LEVEL_TO_ALERT_VARIANT[level] ?? 'info';

/**
 * Renders the `App_Messages` rows that match the given screen, each through the app's existing
 * `Alert` component. Glue only: no styling of its own beyond honouring editor line breaks, and no
 * store access (the context is built by the page). Text is rendered as plain strings.
 */
const AppMessages: React.FC<Props> = ({ criteria }) => {
    const messages = useAppMessages(criteria);
    const renderable = messages
        .map((message) => ({ message, text: resolveMessageText(message) }))
        .filter(({ text }) => text !== '');

    if (renderable.length === 0) {
        return null;
    }

    return (
        <Flex
            direction="column"
            gap={4}
            className={styles.root}
            data-testid="app-messages"
        >
            {renderable.map(({ message, text }) => (
                <Alert
                    key={message.id}
                    variant={toAlertVariant(message.level)}
                    title={message.title ?? undefined}
                    subtitle={<span className={styles.text}>{text}</span>}
                />
            ))}
        </Flex>
    );
};

export default AppMessages;
